// Tailwind classes for badges built in JavaScript, mirroring DesignSystem::BadgeComponent
// (app/components/design_system/badge_component.rb) so client-rendered badges match server-rendered
// ones. Keep the two in sync. Class strings are written out in full so Tailwind can scan them.
const BASE = 'tw:inline-block tw:font-bold tw:leading-none tw:text-center tw:whitespace-nowrap tw:align-middle tw:empty:hidden'

const SIZES = {
  sm: 'tw:px-[0.35em] tw:py-[0.2em] tw:text-[0.65em]',
  md: 'tw:px-[0.4em] tw:py-[0.25em] tw:text-[0.75em]',
  lg: 'tw:px-[1em] tw:py-[0.5em] tw:text-[0.85em]'
}

const UNSTYLED_TEXT = 'tw:text-white'

const SOLID = {
  primary: 'tw:bg-primary-500 tw:text-white',
  secondary: 'tw:bg-secondary-500 tw:text-white',
  success: 'tw:bg-success-500 tw:text-white',
  danger: 'tw:bg-danger-500 tw:text-white',
  warning: 'tw:bg-warning-500 tw:text-secondary-900',
  info: 'tw:bg-info-500 tw:text-white',
  light: 'tw:bg-secondary-50 tw:text-secondary-800 tw:dark:bg-secondary-700 tw:dark:text-secondary-50',
  dark: 'tw:bg-secondary-800 tw:text-white tw:dark:bg-secondary-50 tw:dark:text-secondary-800',
  error: 'tw:bg-danger-500 tw:text-white',
  white: 'tw:bg-white tw:text-secondary-800 tw:dark:bg-secondary-700 tw:dark:text-secondary-50',
  black: 'tw:bg-black tw:text-white',
  transparent: 'tw:bg-transparent tw:text-inherit'
}

const TINT_TEXT = {
  primary: 'tw:text-primary-700 tw:dark:text-primary-300',
  secondary: 'tw:text-secondary-700 tw:dark:text-secondary-200',
  success: 'tw:text-success-700 tw:dark:text-success-300',
  danger: 'tw:text-danger-700 tw:dark:text-danger-300',
  warning: 'tw:text-warning-800 tw:dark:text-warning-300',
  info: 'tw:text-info-700 tw:dark:text-info-300',
  error: 'tw:text-danger-700 tw:dark:text-danger-300'
}

const SUBTLE_FILL = {
  primary: 'tw:bg-primary-500/15',
  secondary: 'tw:bg-secondary-500/15',
  success: 'tw:bg-success-500/15',
  danger: 'tw:bg-danger-500/15',
  warning: 'tw:bg-warning-500/15',
  info: 'tw:bg-info-500/15',
  error: 'tw:bg-danger-500/15'
}

const OUTLINE_BORDER = {
  primary: 'tw:border tw:border-solid tw:border-primary-500',
  secondary: 'tw:border tw:border-solid tw:border-secondary-500',
  success: 'tw:border tw:border-solid tw:border-success-500',
  danger: 'tw:border tw:border-solid tw:border-danger-500',
  warning: 'tw:border tw:border-solid tw:border-warning-500',
  info: 'tw:border tw:border-solid tw:border-info-500',
  error: 'tw:border tw:border-solid tw:border-danger-500'
}

const NEUTRAL_OUTLINE = 'tw:border tw:border-solid tw:border-secondary-100 tw:dark:border-secondary-700'
const NEUTRAL_OUTLINE_VARIANTS = ['light', 'white', 'transparent']

function variantClasses(variant, subtle, outline) {
  if (!SOLID[variant]) return [UNSTYLED_TEXT]
  if (outline && NEUTRAL_OUTLINE_VARIANTS.includes(variant)) return [SOLID[variant], NEUTRAL_OUTLINE]
  if (!(subtle || outline) || !TINT_TEXT[variant]) return [SOLID[variant]]

  return [
    subtle ? SUBTLE_FILL[variant] : 'tw:bg-transparent',
    TINT_TEXT[variant],
    outline ? OUTLINE_BORDER[variant] : null
  ]
}

export function badgeClasses({ variant = 'secondary', subtle = false, outline = false, size = 'md', pill = false, extra = '' } = {}) {
  return [
    BASE,
    SIZES[size] || SIZES.md,
    pill ? 'tw:rounded-full' : 'tw:rounded',
    ...variantClasses(variant, subtle, outline),
    extra
  ].filter(Boolean).join(' ')
}
